import { useEffect, useState} from "react";
import { Box, Button } from "@mui/material";
import socket from "../../socket";
import { getUser } from "../../auth"

import CreateProduct from "./CreateProduct";
import ProductFilter from "../components/ProductFilter";
import ButtonsMenu from "../components/ButtonsMenu";
import InventarioTable from "./InventarioTable";
import DeleteProduct from "./DeleteProduct";
import UpdateProduct from "./UpdateProduct";

const URL = process.env.REACT_APP_API_URL

export default function Inventario() {
  const [empresa, setEmpresa] = useState(null);
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [filters, setFilters] = useState({
    name: "",
    type: "",
    priceRange: [0, 100000000],
  });
  const [selectedAction, setSelectedAction] = useState(null); // "create", "update", "delete"
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [priceIncrease, setPriceIncrease] = useState("");
  
  useEffect(() => {
    const fetchEmpresa = async () => {
      try {
        const user = getUser();
        const response = await fetch(`${URL}/api/empresas/${user.empresa_id}`);
        if (response.ok) {
          const data = await response.json();
          setEmpresa(data);
        } else {
          console.error("Error al obtener la empresa");
        }
      } catch (error) {
        console.error("Error de red al obtener la empresa", error);
      }
    };

    fetchEmpresa();
  }, []);

  const headerData = empresa?.rubro === "Servicios"
    ? ["Id", "Servicio", "Tipo", "Precio", "Referencia"]
    : ["Id", "Producto", "Tipo", "Precio", "Referencia"]; 


    const fetchProducts = () => {
    const user = getUser();
    fetch(`${URL}/api/products/`, {
      headers: {
        "X-Empresa-Id": user?.empresa_id
      }
    })
      .then((res) => {
        if (!res.ok) throw new Error("Error al obtener productos");
        return res.json();
      })
      .then((data) => setProducts(data))
      .catch((error) => console.error(error));
  };

  const fetchProductTypes = () => {
    const user = getUser();
    fetch(`${URL}/api/products/types/`, {
      headers: {
        "X-Empresa-Id": user?.empresa_id
      }
    })
    .then((res) => res.json())
    .then((data) => setProductTypes(data))
    .catch((err) => console.error("Error al cargar tipos:", err));
  };
  
  useEffect(() => {
    fetchProducts();
    fetchProductTypes();
  }, []);
  
  useEffect(() => {
    socket.on("producto_no_eliminado", (data) => {
      alert(`⚠️ No se pudo eliminar el producto con ID ${data.id}: ${data.reason}`);
    });

    socket.on("producto_creado", () => {
      console.log("Nuevo producto creado, refrescando lista...");
      fetchProducts();
    });
  
    return () => {
      socket.off("producto_no_eliminado");
      socket.off("producto_creado");
    };
  }, []);

  const handleAction = (action) => {
    setSelectedAction((prev) => {
      if (prev === action) {
        if (action === "delete") {
          setIsDeleteMode(false);
          setSelectedProducts([]);
        } else if (action === "update") {
          setIsUpdateMode(false);
          setSelectedProducts([]);
        }
        return null;
      }
    
      // Cambia de acción
      if (action === "create") {
        setIsDeleteMode(false);
        setIsUpdateMode(false);
        setSelectedProducts([]);
        setOpenCreateDialog(true);
      } else if (action === "update") {
        setIsUpdateMode(true);
        setIsDeleteMode(false);
        setSelectedProducts([]);
      } else if (action === "delete") {
        setIsDeleteMode(true);
        setIsUpdateMode(false);
        setSelectedProducts([]);
      }

    return action;
    });
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    fetchProducts();
    setSelectedAction(null);
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map((product) => product.id));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: "100%",
        height: "100%",
      }}
    >
      <Box
        height={"12vh"}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          pr: 2,
          mt: 1,
        }}
        >
          <Box sx={{ width: "48vw", mt: 1 }}>
            <ProductFilter 
              onFilterChange={setFilters} 
              productTypes={productTypes}
              empresaRubro={empresa?.rubro} 
            />
          </Box>
          <ButtonsMenu
            selected={selectedAction}
            onAction={handleAction}
          />

      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <InventarioTable 
          headerData={headerData} 
          bodyData={products} 
          onSelect={setSelectedProducts}
          showCheckbox={isDeleteMode || isUpdateMode}
          selectedRows={selectedProducts}
          setSelectedRows={setSelectedProducts}
          checkboxLabel={isDeleteMode ? "Borrar" : "Editar"}
          priceIncrease={priceIncrease}
          filters={filters}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            {isDeleteMode && (
              <DeleteProduct
                selectedProducts={selectedProducts}
                onDelete={() => {
                  setSelectedProducts([]);
                  fetchProducts();
                }}
              />
            )}

            {(isDeleteMode || isUpdateMode) && (
              <Button
                variant="contained"
                color="success"
                onClick={handleSelectAll}
                sx={{ 
                  mt: 2,
                  width: isDeleteMode ? "13.2vw" : "27.2vw",
                  height: "7.6vh",
                  marginLeft: 1.4,
                  fontSize: {md: "12px", sm: "10px"},
                }}
              >
                Seleccionar todos
              </Button>
            )}

          </Box>

          {isUpdateMode && (
            <UpdateProduct
              selectedProducts={products.filter((product) => selectedProducts.includes(product.id))}
              onUpdate={fetchProducts}
              onClose={() => {
                setSelectedAction(null);
                setSelectedProducts([]);
                setIsUpdateMode(false);
              }}
              setPriceIncrease={setPriceIncrease}
              productTypes={productTypes}
              empresaRubro={empresa?.rubro}
            />
          )}

        </Box>
      </Box>

      <CreateProduct
        open={openCreateDialog}
        onClose={handleCloseCreateDialog}
        productTypes={productTypes}
        empresaRubro={empresa?.rubro}
      />
    </Box>
  );
}